import { Send as SendIcon } from '@mui/icons-material';
import CircularProgress from '@mui/material/CircularProgress';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import apiClient from '../api/client';

const ChatContainer = styled.div`
  position: fixed;
  right: 16px;
  top: 72px;
  bottom: 16px;
  width: 92vw;
  max-width: 380px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  @media (min-width: 1200px) {
    right: 24px;
    top: 88px;
    bottom: 24px;
    width: 380px;
  }
`;

const ChatHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChatTitle = styled.h3`
  margin: 0;
  color: white;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1.1rem;
`;

const HeaderActions = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 12px;
`;

const NewButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.12);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 6px 10px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.18);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 3px;
  }
`;

const Message = styled.div`
  padding: 12px 16px;
  border-radius: 12px;
  max-width: 80%;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  line-height: 1.4;
  
  ${props => props.isBot ? `
    background: rgba(255, 255, 255, 0.1);
    color: rgba(255, 255, 255, 0.9);
    align-self: flex-start;
    border-bottom-left-radius: 4px;
  ` : `
    background: rgba(0, 136, 254, 0.3);
    color: white;
    align-self: flex-end;
    border-bottom-right-radius: 4px;
  `}
`;

const InputContainer = styled.div`
  padding: 20px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 12px;
`;

const Input = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  padding: 12px 16px;
  color: white;
  font-family: 'Inter', sans-serif;
  font-size: 0.95rem;
  outline: none;
  transition: all 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:focus {
    border-color: rgba(255, 255, 255, 0.3);
    background: rgba(255, 255, 255, 0.15);
  }
`;

const SendButton = styled.button`
  background: #0088FE;
  border: none;
  border-radius: 8px;
  width: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #0077e6;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`;

const LoadingDots = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  
  span {
    width: 4px;
    height: 4px;
    background: rgba(255, 255, 255, 0.7);
    border-radius: 50%;
    animation: dots 1.5s infinite;
    
    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
  
  @keyframes dots {
    0%, 100% { transform: scale(0.7); opacity: 0.5; }
    50% { transform: scale(1); opacity: 1; }
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 0.9rem;
  margin-top: 8px;
  text-align: center;
`;

const ChatBot = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const init = async () => {
      try {
        let sid = localStorage.getItem('chat_session_id');
        if (!sid) {
          const resp = await apiClient.post('/chat/sessions', { summary: '' });
          sid = resp.data?.id || resp.data?._id;
          if (sid) localStorage.setItem('chat_session_id', sid);
        }
        setSessionId(sid);
        await loadHistory(sid);
      } catch (e) {
        if (messages.length === 0) {
          setMessages([{ text: 'Здравствуйте! Я ваш персональный ассистент. Чем могу помочь?', isBot: true }]);
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadHistory = async (sid) => {
    if (!sid) return;
    try {
      const resp = await apiClient.get(`/chat/sessions/${sid}/messages`, { params: { page: 1, per_page: 100 } });
      const msgs = (resp.data?.data || []).map(m => ({ text: m.text || '', isBot: m.role === 'assistant' }));
      if (msgs.length > 0) {
        setMessages(msgs);
      } else {
        setMessages([{ text: 'Здравствуйте! Я ваш персональный ассистент. Чем могу помочь?', isBot: true }]);
      }
    } catch (e) {
      // ignore
    }
  };

  const appendBot = (text) => setMessages(prev => [...prev, { text, isBot: true }]);
  const appendUser = (text) => setMessages(prev => [...prev, { text, isBot: false }]);

  // Универсальное исполнение инструмента, описанного в tool { name, params }
  const performTool = async (tool) => {
    if (!tool || !tool.name) return null;
    const [method, path] = tool.name.split('_'); // e.g. POST_/clients
    const url = path.replace(/^\//, '');
    const params = tool.params || {};
    try {
      switch (method) {
        case 'GET':
          return (await apiClient.get(`/${url}`, { params })).data;
        case 'POST':
          return (await apiClient.post(`/${url}`, params)).data;
        case 'PUT':
          return (await apiClient.put(`/${url}`, params)).data;
        case 'DELETE':
          return (await apiClient.delete(`/${url}`, { data: params })).data;
        default:
          return null;
      }
    } catch (err) {
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message || err?.response?.data?.error;
      const hint = (status === 409 && url.startsWith('leads'))
        ? 'Лид с такими контактами уже существует. Хотите обновить существующего или открыть его карточку?'
        : null;
      const finalMsg = serverMsg ? (hint ? `${serverMsg}. ${hint}` : serverMsg) : (hint || err.message);
      throw new Error(finalMsg);
    }
  };

  const sendMessage = async (message) => {
    try {
      setIsLoading(true);
      setError(null);

      const lang = localStorage.getItem('lang') || 'ru';
      const { data } = await apiClient.post('/chat/chat', { message, lang, session_id: sessionId });

      // Обработка нового контракта
      const type = data.type;
      if (type === 'ask') {
        appendBot(data.ask || 'Уточните, пожалуйста.');
      } else if (type === 'confirm') {
        appendBot(data.confirm_text || 'Подтвердите действие. Ответьте: да/нет');
      } else if (type === 'tool') {
        appendBot('Выполняю запрос...');
        try {
          const result = await performTool(data.tool);
          appendBot(data.message || 'Готово');
          if (result && typeof result === 'object') {
            // Короткое отформатированное резюме
            const summary = Array.isArray(result) ? `${result.length} элементов` : 'успешно';
            appendBot(`Результат: ${summary}`);
          }
        } catch (e) {
          appendBot(`Ошибка выполнения: ${e.message}`);
        }
      } else if (type === 'answer') {
        appendBot(data.message || '');
      } else {
        appendBot(data.message || '');
      }
    } catch (err) {
      setError('Произошла ошибка при отправке сообщения. Пожалуйста, попробуйте снова.');
      console.error('Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    appendUser(userMessage);
    setInput('');

    await sendMessage(userMessage);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <ChatContainer>
      <ChatHeader>
        <ChatTitle>Виктория AI</ChatTitle>
      </ChatHeader>
      <MessagesContainer>
        {messages.map((message, index) => (
          <Message key={index} isBot={message.isBot}>
            {message.text}
          </Message>
        ))}
        {isLoading && (
          <Message isBot>
            <LoadingDots>
              <span></span>
              <span></span>
              <span></span>
            </LoadingDots>
          </Message>
        )}
        {error && <ErrorMessage>{error}</ErrorMessage>}
        <div ref={messagesEndRef} />
      </MessagesContainer>
      <InputContainer>
        <Input
          type="text"
          placeholder="Введите сообщение..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
        />
        <SendButton
          onClick={handleSend}
          disabled={isLoading || !input.trim()}
          style={{ opacity: isLoading || !input.trim() ? 0.6 : 1 }}
        >
          {isLoading ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <SendIcon sx={{ fontSize: 20 }} />
          )}
        </SendButton>
      </InputContainer>
    </ChatContainer>
  );
};

export default ChatBot; 